import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { NAV_LINKS, PROGRAM_DETAILS } from "@/lib/constants";
import { useEnquiry } from "@/lib/enquiry-context";

export const LogoLockup = () => (
  <img
    src="/assets/logo-full.png"
    alt="Carlton Wellness Center"
    className="h-14 md:h-16 w-auto object-contain"
  />
);

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileProgramsOpen, setMobileProgramsOpen] = useState(false);
  const { openBook } = useEnquiry();
  const location = useLocation();

  const linkCls = (active) =>
    `relative whitespace-nowrap text-[11px] font-semibold tracking-[0.14em] uppercase transition-colors duration-300 ${
      active
        ? "text-[#c9a14a] after:content-[''] after:absolute after:-bottom-2 after:left-0 after:w-full after:h-[2px] after:bg-[#c9a14a]"
        : "text-[#f7f2e8]/85 hover:text-[#c9a14a]"
    }`;

  return (
    <motion.header
      data-testid="site-navigation"
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/95 backdrop-blur-xl border-b border-[#c9a14a]/15"
    >
      <div className="max-w-[1480px] mx-auto px-5 md:px-10 h-[72px] md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="shrink-0" onClick={() => setOpen(false)}>
          <LogoLockup />
        </Link>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {NAV_LINKS.map((link) =>
            link.dropdown ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setDropOpen(true)}
                onMouseLeave={() => setDropOpen(false)}
              >
                <NavLink
                  to={link.to}
                  data-testid="nav-link-wellness-programs"
                  className={linkCls(location.pathname.startsWith("/programs"))}
                >
                  <span className="inline-flex items-center gap-1 whitespace-nowrap">
                    {link.label}
                    <ChevronDown size={13} strokeWidth={2} className="text-[#c9a14a]" />
                  </span>
                </NavLink>
                <AnimatePresence>
                  {dropOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.25 }}
                      data-testid="nav-programs-dropdown"
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-64"
                    >
                      <div className="bg-[#0b0b0b] border border-[#c9a14a]/25 py-3 shadow-2xl">
                        {PROGRAM_DETAILS.map((p) => (
                          <Link
                            key={p.slug}
                            to={`/programs/${p.slug}`}
                            onClick={() => setDropOpen(false)}
                            data-testid={`nav-dropdown-${p.slug}`}
                            className="block px-5 py-2 text-[11px] tracking-[0.08em] text-[#f7f2e8]/80 hover:text-[#c9a14a] hover:bg-[#c9a14a]/5 transition-colors"
                          >
                            {p.title}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={link.label}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => linkCls(isActive)}
              >
                {link.label}
              </NavLink>
            )
          )}
        </nav>

        <button
          onClick={() => openBook("assessment")}
          data-testid="nav-book-assessment-button"
          className="hidden xl:inline-flex btn-gold !py-3 !px-5 whitespace-nowrap shrink-0"
        >
          Book Assessment <ArrowRight size={14} strokeWidth={2} />
        </button>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="xl:hidden text-[#f7f2e8] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            className="xl:hidden bg-[#050505] border-t border-[#c9a14a]/15 overflow-hidden max-h-[calc(100vh-72px)] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {NAV_LINKS.map((link) =>
                link.dropdown ? (
                  <div key={link.label}>
                    <button
                      onClick={() => setMobileProgramsOpen((o) => !o)}
                      data-testid="mobile-nav-programs-toggle"
                      className="flex items-center gap-2 text-[#f7f2e8] font-serif-display text-2xl"
                    >
                      {link.label}
                      <ChevronDown
                        size={18}
                        className={`text-[#c9a14a] transition-transform ${mobileProgramsOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence>
                      {mobileProgramsOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-4 pl-4 flex flex-col gap-3">
                            {PROGRAM_DETAILS.map((p) => (
                              <Link
                                key={p.slug}
                                to={`/programs/${p.slug}`}
                                onClick={() => setOpen(false)}
                                className="text-[#f7f2e8]/75 text-sm"
                              >
                                {p.title}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-[#f7f2e8] font-serif-display text-2xl"
                  >
                    {link.label}
                  </Link>
                )
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  openBook("assessment");
                }}
                data-testid="mobile-book-assessment-button"
                className="btn-gold justify-center mt-2"
              >
                Book Assessment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
